import { pdf } from '@react-pdf/renderer';
import client from 'client';
import { getDataFromS3 } from 'app/main/CompanyList/companyFunctions';

export function formatPrintStamp(date = new Date()) {
  const datePart = date.toLocaleDateString(undefined, {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
  });
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const suffix = date.getHours() >= 12 ? 'p.m.' : 'a.m.';
  const hours = date.getHours() % 12 || 12;
  return `${datePart}, ${hours}:${minutes} ${suffix}`;
}

function isImageBlob(blob) {
  return blob instanceof Blob && blob.size > 0 && !/json|xml|html/i.test(blob.type || '');
}

function signedImageKey(url) {
  try {
    const parsed = new URL(url, window.location.origin);
    if (!parsed.pathname.includes('/aws/signed')) return '';
    return parsed.searchParams.get('image') || '';
  } catch (error) {
    return '';
  }
}

async function requestLogoBlob(request) {
  try {
    const response = await request();
    const blob = response?.data;
    return isImageBlob(blob) ? blob : null;
  } catch (error) {
    return null;
  }
}

async function firstDecodedLogo(candidates) {
  for (let index = 0; index < candidates.length; index += 1) {
    const blob = await candidates[index]();
    if (!blob) continue;
    try {
      return await decodeLogo(blob);
    } catch (error) {
      // This response was not a readable image. Try the next source.
    }
  }
  throw new Error('Logo did not load');
}

async function fetchCompanyLogo(companyLogoSrc) {
  const source = String(companyLogoSrc || '');
  const key = signedImageKey(source);
  const candidates = [];

  if (key) {
    candidates.push(() =>
      requestLogoBlob(() =>
        client.get('/aws/download-file', { params: { image: key }, responseType: 'blob' })
      )
    );
    candidates.push(async () => {
      try {
        const signed = await getDataFromS3({ key });
        const fileLocation = signed?.data?.data?.fileLocation;
        if (typeof fileLocation !== 'string' || !fileLocation.includes('http')) return null;
        return requestLogoBlob(() =>
          client.post('/aws/download-file', { url: fileLocation }, { responseType: 'blob' })
        );
      } catch (error) {
        return null;
      }
    });
    candidates.push(() =>
      requestLogoBlob(() =>
        client.get('/aws/signed', { params: { image: key }, responseType: 'blob' })
      )
    );
  }

  if (source.startsWith('http')) {
    candidates.push(() =>
      requestLogoBlob(() =>
        client.post('/aws/download-file', { url: source }, { responseType: 'blob' })
      )
    );
    candidates.push(async () => {
      try {
        const response = await fetch(source);
        if (!response.ok) return null;
        const blob = await response.blob();
        return isImageBlob(blob) ? blob : null;
      } catch (error) {
        return null;
      }
    });
  }

  return firstDecodedLogo(candidates);
}

async function fetchSameOriginBlob(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error('Logo did not load');
  const blob = await response.blob();
  if (!isImageBlob(blob)) throw new Error('Logo did not load');
  return blob;
}

async function decodeLogo(blob) {
  const bitmap = await createImageBitmap(blob);
  try {
    if (!bitmap.width || !bitmap.height) throw new Error('Logo did not load');
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext('2d');
    context.drawImage(bitmap, 0, 0);
    const src = canvas.toDataURL('image/png');
    if (!src.startsWith('data:image/png;base64,')) throw new Error('Logo did not load');
    return { src, width: bitmap.width, height: bitmap.height };
  } finally {
    if (bitmap.close) bitmap.close();
  }
}

export function fitLogoBox(pixelWidth, pixelHeight, targetHeight, maxWidth) {
  if (!pixelWidth || !pixelHeight) return null;
  let width = (pixelWidth / pixelHeight) * targetHeight;
  let height = targetHeight;
  if (width > maxWidth) {
    width = maxWidth;
    height = (pixelHeight / pixelWidth) * maxWidth;
  }
  return { width, height };
}

function printPdfBlob(blob, previewWindow, frameId, frameTitle) {
  const url = URL.createObjectURL(blob);

  if (previewWindow && !previewWindow.closed) {
    previewWindow.onload = () => {
      previewWindow.focus();
      previewWindow.print();
    };
    previewWindow.location.href = url;
    window.setTimeout(() => URL.revokeObjectURL(url), 180000);
    return;
  }

  const previous = document.getElementById(frameId);
  if (previous) {
    const oldUrl = previous.dataset.url;
    previous.remove();
    if (oldUrl) URL.revokeObjectURL(oldUrl);
  }

  const iframe = document.createElement('iframe');
  iframe.id = frameId;
  iframe.dataset.url = url;
  iframe.title = frameTitle;
  iframe.src = url;
  // Keep the frame laid out but off-screen. A hidden or zero-size frame prints blank in Chrome.
  iframe.style.cssText = 'position:fixed;left:-10000px;top:0;width:820px;height:1060px;border:0;';
  document.body.appendChild(iframe);

  const cleanup = () => {
    if (!iframe.isConnected) return;
    iframe.remove();
    URL.revokeObjectURL(url);
  };

  iframe.onload = () => {
    window.setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (error) {
        window.open(url, '_blank');
      }
    }, 400);
  };

  window.setTimeout(cleanup, 180000);
}

export async function renderPdfBlob({ companyLogoSrc, createDocument }) {
  const loadzLogo = await decodeLogo(
    await fetchSameOriginBlob(`${window.location.origin}/assets/images/logos/loadzlogo.png`)
  );
  const companyLogo = companyLogoSrc ? await fetchCompanyLogo(companyLogoSrc) : null;
  const assets = {
    logoSrc: companyLogo?.src || '',
    logoWidth: companyLogo?.width || 0,
    logoHeight: companyLogo?.height || 0,
    loadzLogoSrc: loadzLogo.src,
    printedAt: formatPrintStamp(),
  };
  return pdf(createDocument(assets)).toBlob();
}

export async function printPdfDocument({
  previewWindow,
  companyLogoSrc,
  frameId,
  frameTitle,
  createDocument,
}) {
  const blob = await renderPdfBlob({ companyLogoSrc, createDocument });
  printPdfBlob(blob, previewWindow, frameId, frameTitle);
}
